import { Fragment, useContext, useCallback, useMemo, useRef } from 'react';
import { IconButton } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  AddOutlined as AddIcon,
  CloseOutlined as CloseIcon,
  CheckRounded as CheckRoundedIcon,
} from '@material-ui/icons';
import PPButton, { VARIENT } from '../../subComponents/button';
import { PatientChartContext } from '../../../contexts/patientChart';
import Input from '../../subComponents/input';
import { PopupV1 as Popup, POPUP_TYPE } from '../../subComponents/popup';
import Modal from '../../subComponents/commonModal/v3';
import {
  MODAL_KEY, RESPONSE_POPUP_KEY, DIMENSION,
} from '../../../constants/patientChart.constant';

/**
 * 
 * @param {{
 *  count: number;
 * }} params 
 * @returns {React.JSX.Element}
 */
const ICD10CodesLoading = ({
  count,
}) => {
  const items = [...new Array(count)].map((_, index) => index);

  return (
    <ul className={`flex flex-column gap-8 m-0 p-0`}>
      {
        items.map((key) => (
          <li key={key} className='flex align-items-center gap-8 bg-white border-15-lighter-grey p-l-2 p-r-1 p-t-1 p-b-1'>
            <Skeleton variant='text' className='flex width-60' />
            <Skeleton variant='text' className='flex width-180' />
          </li>
        ))
      }
    </ul>
  );
}

/**
 * @param {{
 *  data: {
 *    orderId: number;
 *    patientId: number;
 *    product: {};
 *    icd10CodesByTerms: {
 *      data: {
 *        count: number;
 *        icd10Codes: string[];
 *        icd10CodesCollection: Map<string, string>;
 *      };
 *      called: boolean;
 *      loading: boolean;
 *    };
 *    icd10CodesByOrderId: {
 *      data: {
 *        icd10Codes: {
 *          Id: number;
 *          ICDCode: string;
 *          ICDDesc: string;
 *        }[];
 *        recommendedCodes: {
 *          Id: number;
 *          ICDCode: string;
 *          ICDDesc: string;
 *        }[];
 *        icd10CodesCollection: Map<string, {
 *          Id: number;
 *          ICDCode: string;
 *          ICDDesc: string;
 *        }>;
 *      };
 *      called: boolean;
 *      loading: boolean;
 *    };
 *    icd10CodesByPatientId: {
 *      called: boolean;
 *      loading: boolean;
 *    };
 *    addICD10CodesAPI: {
 *      called: boolean;
 *      loading: boolean;
 *    };
 *    removeICD10CodesAPI: {
 *      called: boolean;
 *      loading: boolean;
 *    };
 *  };
 *  action: {
 *    getICD10CodesByTerms: ({}: { terms: string }) => Promise<{}>;
 *    clearICD10CodesByTerms: () => void;
 *    getICD10CodesByOId: ({}: { OrderId: number }) => Promise<{}>;
 *    getICD10CodesByUId: ({}: { UserId: number }) => Promise<{}>;
 *    removeICD10CodeFromCollection: ({}: { code: string }) => void;
 *    addICD10Codes: (codes: {}[]) => Promise<{}>;
 *    removeICD10Codes: (ids: number[]) => Promise<{}>;
 *    getNotesTabInfo: () => void;
 *  };
 * }}
 * @returns 
 */
export const ICD10Codes = ({
  data,
  action,
}) => {
  const {
    icd10Code,
    setICD10Code,
    selectedICD10Codes,
    setSelectedICD10Codes,
    removedICD10Codes,
    setRemovedICD10Codes,
    isAssignedDoctorViewing,
    ModalCollection,
    handleModalOpen,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);
  const {
    orderId,
    patientId,
    icd10CodesByTerms,
    icd10CodesByOrderId,
    icd10CodesByPatientId,
    addICD10CodesAPI,
    removeICD10CodesAPI,
  } = data;
  const {
    getICD10CodesByTerms,
    clearICD10CodesByTerms,
    getICD10CodesByOId,
    getICD10CodesByUId,
    removeICD10CodeFromCollection,
    addICD10Codes,
    removeICD10Codes,
    getNotesTabInfo,
  } = action;
  const icd10CodeCallRef = useRef(0);
  const icd10CodeMaxLimitRef = useRef(60);

  const executeGetICD10CodesByTerms = useCallback(({ terms, maxList }) => {
    if (icd10CodeCallRef.current) {
      clearTimeout(icd10CodeCallRef.current);
    }

    icd10CodeCallRef.current = setTimeout(() => {
      getICD10CodesByTerms({ terms, maxList });
    }, 700);
  }, []);

  /**
   * 
   * @param {{
   *  target: {
   *    value: string;
   *  };
   * }} event
   */
  const handleCodeChange = useCallback((event) => {
    setICD10Code(event.target.value);
    icd10CodeMaxLimitRef.current = 60;

    if (event.target.value?.length === 0) {
      return () => {};
    }

    if (!icd10CodesByTerms.loading) {
      executeGetICD10CodesByTerms({ terms: event.target.value, maxList: icd10CodeMaxLimitRef.current });

      handleModalOpen({
        key: MODAL_KEY.GLOBAL_ICD10_CODES,
        modal: {
          heading: "ICD-10 Codes",
          type: POPUP_TYPE.INFO,
          message: "",
        },
      });
    }

    return () => {};
  }, [icd10CodesByTerms, executeGetICD10CodesByTerms]);

  const handleLoadMore = useCallback(() => {
    if (icd10CodesByTerms.loading) {
      return () => {};
    }

    icd10CodeMaxLimitRef.current += 60;

    /**
     * @description The API supports maxLimit = 500
     */
    if (icd10CodeMaxLimitRef.current > 500) {
      icd10CodeMaxLimitRef.current = 500;
    }

    executeGetICD10CodesByTerms({
      terms: icd10Code,
      maxList: icd10CodeMaxLimitRef.current,
    });
    return () => {};
  }, [icd10Code, icd10CodesByTerms]);

  const isCodeAddedOrSaved = useCallback(({ code }) => {
    if (selectedICD10Codes.has(code)) {
      return true;
    }

    if (icd10CodesByOrderId.data.icd10CodesCollection.has(code)) {
      return true;
    }

    return false;
  }, [selectedICD10Codes, icd10CodesByOrderId])

  const handleAddCodeToQuickSelection = useCallback(({ code }) => {
    if (isCodeAddedOrSaved({ code })) {
      return () => {};
    }

    const codes = new Map(selectedICD10Codes);
    const description = icd10CodesByTerms.data.icd10CodesCollection.get(code) || '';
    codes.set(code, description);
    setSelectedICD10Codes(codes);

    return () => {};
  }, [selectedICD10Codes, icd10CodesByTerms, isCodeAddedOrSaved]);

  const handleRemoveCodeFromQuickSelection = useCallback(({ code }) => {
    const codes = new Map(selectedICD10Codes);
    codes.delete(code);
    setSelectedICD10Codes(codes);
  }, [selectedICD10Codes]);

  // eslint-disable-next-line no-unused-vars
  const handleRemoveCodeFromSavedResponse = useCallback(({ code }) => {
    if (!icd10CodesByOrderId.data.icd10CodesCollection.has(code)) {
      return () => {};
    }

    /**
     * @type {{
     *  Id: number;
     *  ICDCode: string;
     *  ICDDesc: string;
     * }}
     */
    const icd10Code = icd10CodesByOrderId.data.icd10CodesCollection.get(code);
    const removedCodes = new Map(removedICD10Codes);
    removedCodes.set(icd10Code.ICDCode, icd10Code);
    setRemovedICD10Codes(removedCodes);
    removeICD10CodeFromCollection({ code });

    return () => {};
  }, [removedICD10Codes, icd10CodesByOrderId]);

  const handleRemoveCodeFromSavedResponseV2 = useCallback(async ({ id, code, keys }) => {
    handleMultipleModalClose({ keys });
    removeICD10CodeFromCollection({ code });

    try {
      const { status } = await removeICD10Codes([id]);
      if (status === 200) {
        getICD10CodesByOId({ OrderId: orderId });
        getICD10CodesByUId({ UserId: patientId });
      }
    } catch (e) {

    }
  }, [patientId]);

  const handleRemoveCodeFromSavedResponseConfirmation = useCallback(({ id, code }) => {
    handleModalOpen({
      key: RESPONSE_POPUP_KEY.ORDER_ICD10_CODES,
      modal: {
        heading: 'Remove Confirmation',
        type: POPUP_TYPE.INFO,
        message: `Are you sure you want to remove the diagnosis Code ${code}?`,
        id,
        code,
      },
    });
  }, []);

  const handleSaveCodes = useCallback(async () => {
    handleMultipleModalClose({
      keys: [MODAL_KEY.GLOBAL_ICD10_CODES],
    });

    const promises = [];
    if (selectedICD10Codes.size > 0) {
      const selectedCodes = [...selectedICD10Codes.entries()];
      const codesWithDescriptions = selectedCodes.map(([icdCode, icdDesc]) => (`${icdCode} - ${icdDesc}`));
      const orderNote = `entered diagnosis code(s) ${codesWithDescriptions.join(', ')}`;
      const addedCodes = selectedCodes.map(([icdCode, icdDesc]) => ({
        orderId,
        productId: 0,
        orderNote,
        icdCode,
        icdDesc,
      }));
      promises.push(addICD10Codes(addedCodes));
    }

    if (removedICD10Codes.size > 0) {
      const removedCodes = [...removedICD10Codes.values()].map(({ Id }) => Id);
      promises.push(removeICD10Codes(removedCodes));
    }

    await Promise.allSettled(promises);

    getNotesTabInfo();
    setICD10Code('');
    clearICD10CodesByTerms();

    await Promise.allSettled([
      getICD10CodesByOId({ OrderId: orderId }),
      getICD10CodesByUId({ UserId: patientId }),
    ]);
    setSelectedICD10Codes(new Map());
    setRemovedICD10Codes(new Map());
  }, [orderId, patientId, selectedICD10Codes, removedICD10Codes]);

  const createPopupConfig = useCallback((key = '') => {
    const popup = {
      heading: 'Message',
      type: POPUP_TYPE.INFO,
      message: 'API response appears here...',
    };

    if (!ModalCollection.has(key)) {
      return popup;
    }

    return ModalCollection.get(key) || popup;
  }, [ModalCollection]);

  const isLoadingICD10Codes = useMemo(() => {
    if (!icd10CodesByTerms.called) {
      return true;
    }

    return icd10CodesByTerms.loading;
  }, [icd10CodesByTerms]);

  const isLoadingICD10CodeByOIdOrPId = useMemo(() => {
    if (!icd10CodesByOrderId.called) {
      return true;
    }

    if (icd10CodesByOrderId.loading) {
      return true;
    }

    if (!icd10CodesByPatientId.called) {
      return true;
    }

    if (icd10CodesByPatientId.loading) {
      return true;
    }

    return false;
  }, [icd10CodesByOrderId, icd10CodesByPatientId]);

  const hideSelectedICD10Codes = useMemo(() => {
    if (isLoadingICD10CodeByOIdOrPId) {
      return true;
    }

    if (selectedICD10Codes.size === 0) {
      return true;
    }

    return false;
  }, [isLoadingICD10CodeByOIdOrPId, selectedICD10Codes]);

  const hideSavedICD10Codes = useMemo(() => {
    if (isLoadingICD10CodeByOIdOrPId) {
      return true;
    }

    if (icd10CodesByOrderId.data.icd10CodesCollection.size === 0) {
      return true;
    }

    return false;
  }, [isLoadingICD10CodeByOIdOrPId, icd10CodesByOrderId]);

  const disabledLoadMore = useMemo(() => {
    if (!icd10CodesByTerms.called) {
      return true;
    }

    if (icd10CodesByTerms.loading) {
      return true;
    }

    if (icd10CodesByTerms.data.count < icd10CodeMaxLimitRef.current) {
      return true;
    }

    if (icd10CodeMaxLimitRef.current >= 500) {
      return true;
    }

    return false;
  }, [icd10CodesByTerms]);

  const saveInProgress = useMemo(() => {
    if (addICD10CodesAPI.loading) {
      return true;
    }

    if (removeICD10CodesAPI.loading) {
      return true;
    }

    return false;
  }, [addICD10CodesAPI, removeICD10CodesAPI]);

  const disabledSave = useMemo(() => {
    if (!isAssignedDoctorViewing) {
      return true;
    }

    if (isLoadingICD10CodeByOIdOrPId) {
      return true;
    }

    if (saveInProgress) {
      return true;
    }

    if (selectedICD10Codes.size === 0 && removedICD10Codes.size === 0) {
      return true;
    }

    return false;
  }, [isLoadingICD10CodeByOIdOrPId, saveInProgress, selectedICD10Codes, removedICD10Codes, isAssignedDoctorViewing]);

  const noICD10Code = useMemo(() => {
    if (!icd10CodesByTerms.called) {
      return false;
    }

    if (icd10CodesByTerms.loading) {
      return false;
    }

    if (icd10CodesByTerms.data.icd10Codes.length > 0) {
      return false;
    }

    return true;
  }, [icd10CodesByTerms]);

  const icd10Codes = useMemo(() => [...selectedICD10Codes.entries()], [selectedICD10Codes]);

  const recommendedCodes = useMemo(() => {
    return icd10CodesByOrderId.data.recommendedCodes.filter(({ ICDCode }) => {
      const selected = selectedICD10Codes.has(ICDCode);
      const saved = icd10CodesByOrderId.data.icd10CodesCollection.has(ICDCode);
      if (!selected && !saved) {
        return true;
      }
      return false;
    });
  }, [
    selectedICD10Codes,
    icd10CodesByOrderId,
  ]);

  const hideRecommendedCodes = useMemo(() => {
    if (isLoadingICD10CodeByOIdOrPId) {
      return true;
    }

    if (recommendedCodes.length === 0) {
      return true;
    }

    return false;
  }, [recommendedCodes, isLoadingICD10CodeByOIdOrPId]);

  const noRecommendedCodes = useMemo(() => {
    if (isLoadingICD10CodeByOIdOrPId) {
      return false;
    }

    if (recommendedCodes.length > 0) {
      return false;
    }

    return true;
  }, [recommendedCodes, isLoadingICD10CodeByOIdOrPId]);

  const noSavedOrSelectedCodes = useMemo(() => {
    if (isLoadingICD10CodeByOIdOrPId) {
      return false;
    }

    const totalCount = icd10Codes.length + icd10CodesByOrderId.data.icd10Codes.length;
    if (totalCount > 0) {
      return false;
    }

    return true;
  }, [isLoadingICD10CodeByOIdOrPId, icd10Codes, icd10CodesByOrderId]);

  const loadingSkeletonCount = useMemo(() => {
    const totalCount = icd10Codes.length + icd10CodesByOrderId.data.icd10Codes.length;
    if (totalCount < 2) {
      return 2;
    }
    return totalCount;
  }, [icd10Codes, icd10CodesByOrderId]);

  const EditModal = useMemo(() => (
    <Modal
      title={'ICD Codes'}
      open={ModalCollection.has(MODAL_KEY.GLOBAL_ICD10_CODES)}
      style={DIMENSION.SMALL}
      hasClose
      onClose={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.GLOBAL_ICD10_CODES] })}
      responsePopup={ModalCollection.has(RESPONSE_POPUP_KEY.GLOBAL_ICD10_CODES)}
      responsePopupType={createPopupConfig(RESPONSE_POPUP_KEY.GLOBAL_ICD10_CODES).type}
      responsePopupHeading={createPopupConfig(RESPONSE_POPUP_KEY.GLOBAL_ICD10_CODES).heading}
      responsePopupMessage={createPopupConfig(RESPONSE_POPUP_KEY.GLOBAL_ICD10_CODES).message}
      onResponsePopupOk={handleMultipleModalClose.bind(this, { keys: [RESPONSE_POPUP_KEY.GLOBAL_ICD10_CODES] })}
    >
      <div className='flex flex-column flex-fill m-5 patient-chart-container gap-16'>
        <div className='flex flex-column flex-fill'>
          <ul className='flex flex-column gap-8 m-0 p-0 max-height-160'>
            {
              icd10CodesByTerms.data.icd10Codes.map((code) => (
                <li key={code} className='flex align-items-center justify-content-space-between gap-8 border-dark-primary-v2 bg-white p-1'>
                  <p className='m-0 p-0 line-20'>
                    <span className='bold m-r-1'>{code}</span> {icd10CodesByTerms.data.icd10CodesCollection.get(code)}
                  </p>

                  <IconButton
                    aria-label='add-icd10-code'
                    size='small'
                    className='primary-button'
                    onClick={handleAddCodeToQuickSelection.bind(this, { code })}
                  >
                    {
                      isCodeAddedOrSaved({ code })
                        ? <CheckRoundedIcon fontSize='small' />
                        : <AddIcon fontSize='small' />
                    }
                  </IconButton>
                </li>
              ))
            }
            {
              isLoadingICD10Codes && (
                <li className='flex align-items-center gap-8 border-15-lighter-grey bg-white p-1 height-24'>
                  <Skeleton variant='text' className='flex width-60' />
                  <Skeleton variant='text' className='flex width-180' />
                </li>
              )
            }
            {
              noICD10Code && (
                <li className='flex align-items-center justify-content-space-between gap-8 bg-white p-1'>
                  <p className='m-0 p-0 line-20'>
                    No ICD-10 code to be displayed!
                  </p>
                </li>
              )
            }
          </ul>
        </div>

        <div className='flex justify-content-space-between'>
          <PPButton
            label='LOAD MORE'
            variant={VARIENT.contained}
            disabled={disabledLoadMore}
            loading={isLoadingICD10Codes}
            className='width-240'
            onClick={handleLoadMore}
          />
          <PPButton
            label='CLOSE'
            className='width-240'
            onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.GLOBAL_ICD10_CODES] })}
          />
        </div>
      </div>
    </Modal>
  ), [ModalCollection, createPopupConfig, icd10CodesByTerms, isLoadingICD10Codes, disabledLoadMore, isCodeAddedOrSaved, handleMultipleModalClose, handleAddCodeToQuickSelection]);

  const RemoveICD10CodeConfirmation = useMemo(() => {
    const isOpen = ModalCollection.has(RESPONSE_POPUP_KEY.ORDER_ICD10_CODES);
    /**
     * @type {{
     *  heading: string;
     *  type: string;
     *  message: string;
     *  id: number;
     *  code: string;
     * }}
     */
    const popupConfig = ModalCollection.get(RESPONSE_POPUP_KEY.ORDER_ICD10_CODES) || {
      heading: '',
      type: POPUP_TYPE.INFO,
      message: '',
      id: 0,
      code: '',
    };
    return (
      <Popup open={isOpen} title={popupConfig.heading} type={popupConfig.type}>
        <div className='flex flex-column'>
          <div className='flex align-items-center m-b-5 font-roboto'>{popupConfig.message}</div>
          <div className='flex align-items-center justify-content-center'>
            <PPButton
              label='Yes'
              variant={VARIENT.contained}
              endGutter
              onClick={handleRemoveCodeFromSavedResponseV2.bind(this, {
                keys: [RESPONSE_POPUP_KEY.ORDER_ICD10_CODES],
                id: popupConfig.id,
                code: popupConfig.code,
              })}
            />
            <PPButton
              label='No'
              variant={VARIENT.outlined}
              endGutter
              onClick={handleMultipleModalClose.bind(this, { keys: [RESPONSE_POPUP_KEY.ORDER_ICD10_CODES] })}
            />
          </div>
        </div>
      </Popup>
    );
  }, [ModalCollection, handleRemoveCodeFromSavedResponseV2, handleMultipleModalClose]);

  return (
    <Fragment>
      <div className='flex flex-column gap-16 min-height-100'>
        <div className='flex flex-column gap-4'>
          <div className='flex align-items-center height-26'>
            <div className='flex'>Recommended Codes</div>
          </div>

          {
            isLoadingICD10CodeByOIdOrPId && <ICD10CodesLoading count={1} />
          }

          <ul className={`flex flex-column gap-8 m-0 p-0 ${hideRecommendedCodes && 'd-none'}`}>
            {
              recommendedCodes.map((item) => (
                <li key={item.Id} className='flex align-items-center justify-content-space-between gap-8 border-15-primary bg-white p-l-2 p-r-1 p-t-1 p-b-1'>
                  <p className='m-0 p-0 line-20'>
                    <span className='bold m-r-1'>{item.ICDCode}</span> {item.ICDDesc}
                  </p>
    
                  <IconButton
                    aria-label='add-icd10-code'
                    size='small'
                    className='primary-button'
                    onClick={handleAddCodeToQuickSelection.bind(this, { code: item.ICDCode })}
                  >
                    {
                      isCodeAddedOrSaved({ code: item.ICDCode })
                        ? <CheckRoundedIcon fontSize='small' />
                        : <AddIcon fontSize='small' />
                    }
                  </IconButton>
                </li>
              ))
            }
          </ul>

          {
            noRecommendedCodes && (
              <ul className='flex flex-column gap-8 m-0 p-0'>
                <li className='flex align-items-center justify-content-space-between gap-8 border-primary-v4-2 bg-white p-l-2 p-r-1 p-t-1 p-b-1'>
                  <p className='m-0 p-0 line-20'>
                    No other recommendations!
                  </p>
                </li>
              </ul>
            )
          }
        </div>

        <div className='flex flex-column gap-4'>
          <div className='flex align-items-center height-26'>
            Search Codes
          </div>
          <div className='flex align-items-center'>
            <Input
              name='icd10Code'
              type='text'
              className='flex flex-fill'
              fontSize='16'
              max={255}
              value={icd10Code}
              onChange={handleCodeChange}
              placeholder='Type ICD code here'
              autoComplete='off'
            />
          </div>
        </div>

        <div className='flex flex-column gap-4'>
          <div className='flex align-items-center height-26'>
            <div className='flex'>Saved Codes</div>
          </div>

          <div className='flex flex-column gap-8'>
            <ul className={`flex flex-column gap-8 m-0 p-0 ${hideSelectedICD10Codes && 'd-none'}`}>
              {
                icd10Codes.map(([code, description]) => (
                  <li key={code} className='flex align-items-center justify-content-space-between gap-8 bg-white border-15-primary p-l-2 p-r-1 p-t-1 p-b-1'>
                    <p className='m-0 p-0 line-20'>
                      <span className='bold m-r-1'>{code}</span> {description}
                    </p>

                    <IconButton
                      aria-label='remove-icd10-code'
                      size='small'
                      className='red-v1-button'
                      onClick={handleRemoveCodeFromQuickSelection.bind(this, { code })}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </li>
                ))
              }
            </ul>

            <ul className={`flex flex-column gap-8 m-0 p-0 ${hideSavedICD10Codes && 'd-none'}`}>
              {
                icd10CodesByOrderId.data.icd10Codes.map((item) => (
                  <li key={item.Id} className='flex align-items-center justify-content-space-between gap-8 bg-dark-primary-v2 p-l-2 p-r-1 p-t-1 p-b-1'>
                    <p className='m-0 p-0 line-20 color-white'>
                      <span className='bold m-r-1'>{item.ICDCode}</span> {item.ICDDesc}
                    </p>
      
                    <IconButton
                      aria-label='remove-icd10-code'
                      size='small'
                      className='white-button'
                      onClick={handleRemoveCodeFromSavedResponseConfirmation.bind(this, { id: item.Id, code: item.ICDCode })}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </li>
                ))
              }
            </ul>

            {
              isLoadingICD10CodeByOIdOrPId && <ICD10CodesLoading count={loadingSkeletonCount} />
            }

            {
              noSavedOrSelectedCodes && (
                <ul className='flex flex-column gap-8 m-0 p-0'>
                  <li className='flex align-items-center justify-content-space-between gap-8 border-primary-v4-2 bg-white p-l-2 p-r-1 p-t-1 p-b-1'>
                    <p className='m-0 p-0 line-20'>
                      No code found!
                    </p>
                  </li>
                </ul>
              )
            }
          </div>
        </div>

        <div className='flex flex-column gap-8'>
          <PPButton
            label='Save Codes'
            disabled={disabledSave}
            onClick={handleSaveCodes}
            loading={saveInProgress}
          />
        </div>
      </div>

      {EditModal}

      {RemoveICD10CodeConfirmation}
    </Fragment>
  );
};

export default ICD10Codes;
